import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { inr, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Textarea } from "@/components/ui/textarea";
import { Plus, DotsThreeVertical, Printer, Receipt, CheckCircle, Circle, ClockCounterClockwise, Sparkle, CurrencyInr, Rocket } from "@phosphor-icons/react";
import { toast } from "sonner";
import CustomerLink from "@/components/CustomerLink";
import TransferDialog from "@/components/TransferDialog";
import { useAuth } from "@/context/AuthContext";

const STATUS_TONE = {
  Draft: "bg-slate-50 text-slate-500 border-slate-300 border-dashed",
  New: "bg-slate-100 text-slate-700 border-slate-200",
  "Under Verification": "bg-blue-50 text-blue-700 border-blue-200",
  Verification: "bg-blue-50 text-blue-700 border-blue-200",
  "Documents Required": "bg-purple-50 text-purple-700 border-purple-200",
  "In Progress": "bg-amber-50 text-amber-700 border-amber-200",
  Submitted: "bg-indigo-50 text-indigo-700 border-indigo-200",
  Approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Certificate Issued": "bg-emerald-100 text-emerald-800 border-emerald-300",
  Completed: "bg-emerald-600 text-white border-emerald-600",
  Rejected: "bg-red-50 text-red-700 border-red-200",
};

const STATUS_STEPS = ["New", "Under Verification", "Documents Required", "In Progress", "Submitted", "Approved", "Certificate Issued", "Completed"];
const STAGE_WORKFLOW = [
  "New Application", "Documents Pending", "Documents Verified", "Payment Pending", "Payment Received",
  "Application Submitted", "Government Processing", "Approved", "Certificate Ready", "Delivered", "Completed",
];
const PAY_METHODS = ["Cash", "UPI", "PhonePe", "Google Pay", "Paytm", "Card", "Net Banking", "NEFT", "RTGS", "IMPS", "Wallet", "Cheque"];

const DEFAULT_FORM = {
  customer_id: "", service_id: "", notes: "", priority: "Normal",
  manual_service: false, manual_service_name: "",
  govt_fee: 0, service_charge: 0, printing_charge: 0,
  scanning_charge: 0, affidavit_charge: 0, lamination_charge: 0,
  discount: 0, gst_percent: 18,
  portal_app_no: "", token_no: "", assigned_operator: "", expected_completion: "", remarks: "",
  reference_no: "", tags: "", branch_id: "",
  advance_amount: 0, advance_method: "Cash", advance_txn_ref: "",
};

const fileToB64 = (f) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(String(r.result).split(",")[1]);
  r.onerror = rej;
  r.readAsDataURL(f);
});

const PRIORITY_TONE = {
  Normal: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Urgent: "bg-amber-50 text-amber-700 border-amber-300",
  Emergency: "bg-red-50 text-red-700 border-red-300",
};
const PRIORITY_DOT = { Normal: "bg-emerald-500", Urgent: "bg-amber-500", Emergency: "bg-red-500" };

export default function Applications() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const isAdmin = ["admin", "super_admin", "branch_admin", "head_office_admin"].includes(user?.role);
  const [transfer, setTransfer] = useState(null);
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [services, setServices] = useState([]);
  const [branches, setBranches] = useState([]);
  const [attachFiles, setAttachFiles] = useState([]);
  const [autoPrint, setAutoPrint] = useState(false);
  const [open, setOpen] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [pay, setPay] = useState(null);
  const [edit, setEdit] = useState(null);
  const [track, setTrack] = useState(null);
  const [advanceStage, setAdvanceStage] = useState(null);   // { app, stage, note }
  const [history, setHistory] = useState([]);
  const [aiExplain, setAiExplain] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [priorityFilter, setPriorityFilter] = useState("All");
  const [custSearch, setCustSearch] = useState("");
  const [svcSearch, setSvcSearch] = useState("");
  const [svcCategory, setSvcCategory] = useState("All");
  const [params, setParams] = useSearchParams();

  const load = useCallback(async () => {
    setRows((await api.get("/applications")).data);
  }, []);

  useEffect(() => {
    load();
    api.get("/customers").then((r) => setCustomers(r.data));
    api.get("/services").then((r) => setServices(r.data));
  }, [load]);

  useEffect(() => {
    if (isSuper) api.get("/branches", { params: { branch_id: "" } }).then((r) => setBranches(r.data || [])).catch(() => {});
  }, [isSuper]);

  useEffect(() => {
    if (receipt && autoPrint) {
      const t = setTimeout(() => { window.print(); setAutoPrint(false); }, 600);
      return () => clearTimeout(t);
    }
  }, [receipt, autoPrint]);

  // deep link from global search or dashboard priority filter
  useEffect(() => {
    const id = params.get("id");
    if (id && rows.length) {
      const r = rows.find((x) => x.id === id);
      if (r) { setReceipt(r); setParams({}); }
    }
    const pf = params.get("priority");
    if (pf && ["Normal", "Urgent", "Emergency"].includes(pf)) setPriorityFilter(pf);
    if (params.get("new") === "1") { setOpen(true); setParams({}); }
  }, [params, rows, setParams]);

  const svc = useMemo(
    () => services.find((s) => s.id === form.service_id),
    [services, form.service_id],
  );
  useEffect(() => {
    if (svc) setForm((f) => ({ ...f, govt_fee: svc.default_govt_fee, service_charge: svc.default_service_charge }));
  }, [svc]);

  const totals = useMemo(() => {
    const sub = Number(form.govt_fee || 0) + Number(form.service_charge || 0) + Number(form.printing_charge || 0)
              + Number(form.scanning_charge || 0) + Number(form.affidavit_charge || 0) + Number(form.lamination_charge || 0);
    const taxable = Math.max(0, sub - Number(form.discount || 0));
    const gst = +(taxable * Number(form.gst_percent || 0) / 100).toFixed(2);
    return { sub, taxable, gst, total: +(taxable + gst).toFixed(2) };
  }, [form]);

  const submit = async (opts = {}) => {
    const { draft = false, andNew = false, print = false } = opts;
    if (!form.customer_id) { toast.error("Select customer"); return; }
    if (form.manual_service ? !form.manual_service_name.trim() : !form.service_id) {
      toast.error("Select a service or type the service name"); return;
    }
    if ((form.notes || "").length > 1000) { toast.error("Description is limited to 1000 characters"); return; }
    try {
      const payload = {
        ...form,
        service_id: form.manual_service ? "" : form.service_id,
        manual_service_name: form.manual_service ? form.manual_service_name.trim() : "",
        tags: form.tags ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : [],
        save_as_draft: draft,
      };
      const { data } = await api.post("/applications", payload);
      // Upload attached documents to the customer's library
      for (const f of attachFiles) {
        try {
          const b64 = await fileToB64(f);
          await api.post(`/customers/${form.customer_id}/documents`, {
            category: "Application", doc_type: "Application Upload",
            file_name: f.name, mime: f.type || "application/octet-stream",
            data_base64: b64, notes: `Attached to ${data.app_no}`,
          });
        } catch { toast.error(`Upload failed: ${f.name}`); }
      }
      let final = data;
      const adv = Number(form.advance_amount || 0);
      if (!draft && adv > 0 && data.total > 0) {
        try {
          const payRes = await api.post(`/applications/${data.id}/pay`, {
            method: form.advance_method || "Cash",
            amount: adv,
            txn_ref: form.advance_txn_ref || "",
          });
          final = payRes.data;
          toast.success(`${data.app_no} · Advance ₹${adv} received`);
        } catch (payErr) {
          toast.error(`Application saved but advance failed: ${formatApiError(payErr.response?.data?.detail)}`);
        }
      } else {
        toast.success(draft ? `Draft ${data.app_no} saved` : `Application ${data.app_no} created`);
      }
      load();
      setAttachFiles([]);
      if (andNew) { setForm({ ...DEFAULT_FORM, branch_id: form.branch_id }); return; }
      setOpen(false); setForm(DEFAULT_FORM);
      if (!draft) { setAutoPrint(print); setReceipt(final); }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const promoteService = async (r) => {
    try {
      await api.post(`/applications/${r.id}/promote-service`);
      toast.success(`"${r.service_name}" saved to Service Master`);
      load();
      api.get("/services").then((res) => setServices(res.data));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveEdit = async () => {
    try {
      const { data } = await api.patch(`/applications/${edit.id}`, {
        portal_app_no: edit.portal_app_no,
        token_no: edit.token_no,
        assigned_operator: edit.assigned_operator,
        expected_completion: edit.expected_completion,
        remarks: edit.remarks,
        priority: edit.priority,
      });
      toast.success("Application updated"); setEdit(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openTrack = async (r) => {
    setTrack(r); setAiExplain(""); setHistory([]);
    try {
      const { data } = await api.get(`/applications/${r.id}/history`);
      setHistory(data.history || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const askAi = async () => {
    if (!track) return;
    setAiLoading(true); setAiExplain("");
    try {
      const { data } = await api.post(`/ai/explain-status`, { application_id: track.id, language: "English" });
      setAiExplain(data.explanation || "");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "AI failed"); }
    finally { setAiLoading(false); }
  };

  const changeStatus = async (id, status) => {
    try { await api.patch(`/applications/${id}/status`, { status }); toast.success(`Status → ${status}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doPay = async () => {
    try {
      const { data } = await api.post(`/applications/${pay.id}/pay`, {
        method: pay.method,
        txn_ref: pay.txn_ref || "",
        amount: pay.amount != null ? Number(pay.amount) : undefined,
      });
      const paidNow = pay.amount != null ? Number(pay.amount) : (pay.app.total || 0);
      toast.success(`${data.payment_status === "Paid" ? "Paid in full" : "Partial payment recorded"} · ${inr(paidNow)}`);
      setPay(null); load(); setReceipt(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const quickCashPay = async (row) => {
    try {
      const { data } = await api.post(`/applications/${row.id}/pay`, { method: "Cash", txn_ref: "" });
      toast.success(`Cash payment recorded · ${inr(row.total)}`);
      load(); setReceipt(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="p-8 max-w-[1400px]">
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-orange-600 font-semibold mb-2">Service Workflow</div>
          <h1 className="text-4xl font-bold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>Applications & Billing</h1>
          <p className="text-sm text-slate-600 mt-2">Aaple Sarkar · MahaOnline · CSC — end-to-end with GST invoicing.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-application-btn" className="bg-orange-600 hover:bg-orange-700 text-white h-11 gap-2 hover:-translate-y-px hover:shadow-md transition-[transform,box-shadow,background-color] duration-200">
              <Plus size={18} weight="bold" /> New Application
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto bg-white">
            <DialogHeader><DialogTitle style={{ fontFamily: "var(--font-heading)" }}>New Service Application</DialogTitle></DialogHeader>

            <SectionHeading>Customer & Service</SectionHeading>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <F label="Customer *">
                <div className="relative mb-2">
                  <Input value={custSearch} onChange={(e) => setCustSearch(e.target.value)} placeholder="Search by name, mobile, ID, Aadhaar…" className="h-9 text-sm" />
                </div>
                <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                  <SelectTrigger data-testid="app-customer-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {customers
                      .filter((c) => {
                        if (!custSearch) return true;
                        const q = custSearch.toLowerCase();
                        return (c.name || "").toLowerCase().includes(q)
                          || (c.mobile || "").includes(custSearch)
                          || (c.customer_no || "").toLowerCase().includes(q)
                          || (c.aadhaar || "").includes(custSearch)
                          || (c.pan || "").toLowerCase().includes(q);
                      })
                      .slice(0, 200)
                      .map((c) => <SelectItem key={c.id} value={c.id}>{c.customer_no} · {c.name} · {c.mobile}</SelectItem>)}
                  </SelectContent>
                </Select>
              </F>
              <F label="Service *">
                <div className="mb-2">
                  <button
                    type="button"
                    data-testid="app-service-other-toggle"
                    onClick={() => setForm({ ...form, manual_service: !form.manual_service, service_id: "" })}
                    className={`px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${
                      form.manual_service ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300 hover:border-slate-500"
                    }`}
                  >
                    ✎ Other (Manual Typing)
                  </button>
                </div>
                {form.manual_service ? (
                  <>
                    <Input
                      data-testid="app-manual-service-input"
                      value={form.manual_service_name}
                      onChange={(e) => setForm({ ...form, manual_service_name: e.target.value })}
                      placeholder="Enter service name manually…"
                    />
                    <div className="text-[11px] text-amber-700 mt-1">Applies only to this application — an admin can later save it to the Service Master.</div>
                  </>
                ) : (
                <>
                <div className="mb-2 grid grid-cols-2 gap-2">
                  <Input value={svcSearch} onChange={(e) => setSvcSearch(e.target.value)} placeholder="Search service…" className="h-9 text-sm" data-testid="app-service-search" />
                  <Select value={svcCategory} onValueChange={setSvcCategory}>
                    <SelectTrigger className="h-9" data-testid="app-service-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="All">All categories</SelectItem>
                      {Array.from(new Set(services.map((s) => s.category).filter(Boolean))).sort().map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Select value={form.service_id} onValueChange={(v) => setForm({ ...form, service_id: v })}>
                  <SelectTrigger data-testid="app-service-select"><SelectValue placeholder="Select service" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {services
                      .filter((s) => (svcCategory === "All" || s.category === svcCategory)
                        && (!svcSearch || s.name.toLowerCase().includes(svcSearch.toLowerCase()) || (s.department || "").toLowerCase().includes(svcSearch.toLowerCase())))
                      .slice(0, 300)
                      .map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          <span>{s.name}</span>
                          <span className="text-xs text-slate-400 ml-2">{s.category}</span>
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                </>
                )}
              </F>
              <div className="md:col-span-2 space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-600">Priority</Label>
                <div className="flex gap-2 flex-wrap">
                  {["Normal", "Urgent", "Emergency"].map((p) => (
                    <button
                      key={p} type="button"
                      onClick={() => setForm({ ...form, priority: p })}
                      data-testid={`app-priority-${p.toLowerCase()}`}
                      className={`flex items-center gap-2 px-4 py-2 rounded-full border transition-all ${
                        form.priority === p
                          ? (p === "Emergency" ? "bg-red-600 text-white border-red-600" : p === "Urgent" ? "bg-amber-500 text-white border-amber-500" : "bg-emerald-600 text-white border-emerald-600")
                          : "bg-white text-slate-600 border-slate-300 hover:border-slate-500"
                      }`}
                    >
                      <span className={`w-2 h-2 rounded-full ${PRIORITY_DOT[p]}`}></span>
                      <span className="text-sm font-medium">{p}</span>
                    </button>
                  ))}
                </div>
              </div>
              {svc && (
                <div className="md:col-span-2 flex flex-wrap gap-2">
                  {svc.category && <Badge className="bg-slate-900 text-white hover:bg-slate-900">{svc.category}</Badge>}
                  {svc.department && <Badge variant="outline" className="border-orange-300 text-orange-700 bg-orange-50">{svc.department}</Badge>}
                </div>
              )}
            </div>

            {svc && svc.required_docs && svc.required_docs.length > 0 && (
              <div className="mt-4 rounded-md border border-orange-200 bg-orange-50/50 p-4">
                <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-orange-700 mb-2">Required Documents Checklist</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                  {svc.required_docs.map((d) => (
                    <div key={d} className="flex items-center gap-2 text-sm text-slate-700">
                      <Circle size={14} className="text-orange-600 shrink-0" weight="regular" /> {d}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <SectionHeading>Portal Tracking</SectionHeading>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <F label="Aaple Sarkar / MahaOnline / CSC App No."><Input data-testid="app-portal-no" value={form.portal_app_no} onChange={(e) => setForm({ ...form, portal_app_no: e.target.value })} placeholder="e.g. AS20260713XXXX" /></F>
              <F label="Token Number"><Input value={form.token_no} onChange={(e) => setForm({ ...form, token_no: e.target.value })} /></F>
              <F label="Assigned Staff"><Input value={form.assigned_operator} onChange={(e) => setForm({ ...form, assigned_operator: e.target.value })} /></F>
              <F label="Due Date">
                <Input type="date" data-testid="app-due-date" value={form.expected_completion} onChange={(e) => setForm({ ...form, expected_completion: e.target.value })} />
              </F>
              <F label="Branch">
                {isSuper ? (
                  <Select value={form.branch_id || "__own__"} onValueChange={(v) => setForm({ ...form, branch_id: v === "__own__" ? "" : v })}>
                    <SelectTrigger data-testid="app-branch-select"><SelectValue placeholder="My branch (default)" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__own__">My Branch (default)</SelectItem>
                      {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.code} · {b.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="h-10 px-3 flex items-center rounded-md border border-slate-200 bg-slate-50 text-sm text-slate-600" data-testid="app-branch-locked">
                    {user?.branch ? `${user.branch.code} · ${user.branch.name}` : "Your branch"}
                    <span className="ml-2 text-[10px] text-slate-400">(locked)</span>
                  </div>
                )}
              </F>
              <F label="Reference Number"><Input data-testid="app-reference-no" value={form.reference_no} onChange={(e) => setForm({ ...form, reference_no: e.target.value })} placeholder="External ref / file no." /></F>
              <div className="md:col-span-2"><F label="Remarks"><Input value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} /></F></div>
            </div>

            <SectionHeading>Details & Attachments</SectionHeading>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <F label="Tags (comma separated)"><Input data-testid="app-tags" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="e.g. pan, urgent-client" /></F>
              <F label="Document Upload (saved to customer's library)">
                <Input data-testid="app-file-upload" type="file" multiple accept="image/*,.pdf" className="pt-1.5" onChange={(e) => setAttachFiles(Array.from(e.target.files || []))} />
                {attachFiles.length > 0 && <div className="text-[11px] text-slate-500 mt-1">{attachFiles.length} file(s) selected</div>}
              </F>
              <div className="md:col-span-2">
                <F label={`Description (${(form.notes || "").length}/1000)`}>
                  <Textarea data-testid="app-description" rows={3} maxLength={1000} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Application details…" />
                </F>
              </div>
            </div>

            <SectionHeading>Billing (GST Auto-calculated)</SectionHeading>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <NumField label="Government Fee" testid="app-govt-fee" value={form.govt_fee} onChange={(v) => setForm({ ...form, govt_fee: v })} />
              <NumField label="Service Charge" testid="app-svc-charge" value={form.service_charge} onChange={(v) => setForm({ ...form, service_charge: v })} />
              <NumField label="Printing" value={form.printing_charge} onChange={(v) => setForm({ ...form, printing_charge: v })} />
              <NumField label="Scanning" value={form.scanning_charge} onChange={(v) => setForm({ ...form, scanning_charge: v })} />
              <NumField label="Affidavit" value={form.affidavit_charge} onChange={(v) => setForm({ ...form, affidavit_charge: v })} />
              <NumField label="Lamination" value={form.lamination_charge} onChange={(v) => setForm({ ...form, lamination_charge: v })} />
              <NumField label="Discount" value={form.discount} onChange={(v) => setForm({ ...form, discount: v })} />
              <NumField label="GST %" value={form.gst_percent} onChange={(v) => setForm({ ...form, gst_percent: v })} />
            </div>

            <div className="md:col-span-2 bg-slate-50 border border-slate-200 rounded-md p-4 mt-4">
              <Row label="Subtotal" value={inr(totals.sub)} />
              <Row label="After Discount" value={inr(totals.taxable)} />
              <Row label={`GST (${form.gst_percent}%)`} value={inr(totals.gst)} />
              <div className="flex justify-between mt-2 pt-2 border-t border-slate-300">
                <span className="text-sm font-semibold">Total Payable</span>
                <span className="text-2xl font-bold text-orange-600" style={{ fontFamily: "var(--font-heading)" }} data-testid="app-total-preview">{inr(totals.total)}</span>
              </div>
            </div>

            {/* Advance Payment */}
            <div className="md:col-span-2 rounded-md border border-emerald-200 bg-emerald-50/40 p-4 mt-2" data-testid="app-advance-section">
              <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-emerald-700 mb-3 flex items-center gap-2">
                <CurrencyInr size={12} weight="fill" /> Advance Payment (Optional)
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <NumField label={`Advance Amount (max ₹${totals.total})`} value={form.advance_amount} onChange={(v) => setForm({ ...form, advance_amount: Math.min(Number(v || 0), totals.total) })} />
                <F label="Payment Mode">
                  <Select value={form.advance_method} onValueChange={(v) => setForm({ ...form, advance_method: v })}>
                    <SelectTrigger data-testid="app-advance-method"><SelectValue /></SelectTrigger>
                    <SelectContent>{PAY_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                </F>
                {form.advance_method !== "Cash" && form.advance_amount > 0 && (
                  <F label="Txn Ref / UTR (optional)">
                    <Input data-testid="app-advance-txn" value={form.advance_txn_ref} onChange={(e) => setForm({ ...form, advance_txn_ref: e.target.value })} placeholder="UTR / RRN" />
                  </F>
                )}
              </div>
              {Number(form.advance_amount || 0) > 0 && totals.total > 0 && (
                <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                  <div className="p-2 rounded bg-white border border-slate-200">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Grand Total</div>
                    <div className="font-bold text-slate-900">{inr(totals.total)}</div>
                  </div>
                  <div className="p-2 rounded bg-white border border-emerald-300">
                    <div className="text-[10px] uppercase tracking-wider text-emerald-700">Advance Paid</div>
                    <div className="font-bold text-emerald-700">{inr(Number(form.advance_amount))}</div>
                  </div>
                  <div className="p-2 rounded bg-white border border-amber-300">
                    <div className="text-[10px] uppercase tracking-wider text-amber-700">Balance Due</div>
                    <div className="font-bold text-amber-700">{inr(Math.max(0, totals.total - Number(form.advance_amount)))}</div>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter className="mt-6 flex-wrap gap-2">
              <Button variant="outline" onClick={() => setOpen(false)} data-testid="app-cancel-btn">Cancel</Button>
              <Button variant="outline" onClick={() => submit({ draft: true })} data-testid="app-save-draft-btn" className="border-slate-400 text-slate-700 hover:bg-slate-100">Save Draft</Button>
              <Button variant="outline" onClick={() => submit({ andNew: true })} data-testid="app-save-new-btn" className="border-orange-300 text-orange-700 hover:bg-orange-50">Save &amp; New</Button>
              <Button variant="outline" onClick={() => submit({})} data-testid="app-generate-invoice-btn" className="border-slate-900 text-slate-900 hover:bg-slate-100">Generate Invoice</Button>
              <Button data-testid="app-create-btn" onClick={() => submit({ print: true })} className="bg-orange-600 hover:bg-orange-700 text-white gap-2">
                <Printer size={16} />
                {Number(form.advance_amount || 0) > 0 ? "Save + Advance + Print" : "Save & Print Receipt"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mb-4 flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-2 flex-wrap" data-testid="priority-filter-tabs">
          {["All", "Emergency", "Urgent", "Normal"].map((p) => {
            const count = p === "All" ? rows.length : rows.filter((r) => (r.priority || "Normal") === p).length;
            const isActive = priorityFilter === p;
            const toneActive = p === "Emergency" ? "bg-red-600 text-white border-red-600" : p === "Urgent" ? "bg-amber-500 text-white border-amber-500" : p === "Normal" ? "bg-emerald-600 text-white border-emerald-600" : "bg-slate-900 text-white border-slate-900";
            return (
              <button
                key={p}
                onClick={() => setPriorityFilter(p)}
                data-testid={`filter-priority-${p.toLowerCase()}`}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium transition-all ${isActive ? toneActive : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"}`}
              >
                {p !== "All" && <span className={`w-2 h-2 rounded-full ${PRIORITY_DOT[p]}`}></span>}
                {p}
                <span className={`text-xs ${isActive ? "opacity-90" : "text-slate-500"}`}>({count})</span>
              </button>
            );
          })}
        </div>
      </div>

      <Card className="bg-white border-slate-200 overflow-hidden" data-testid="applications-table">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="text-xs uppercase tracking-[0.15em] text-slate-500">Priority</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-slate-500">App #</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-slate-500">Customer</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-slate-500">Service</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-slate-500">Portal #</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-slate-500">Status</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-slate-500 text-right">Total</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-slate-500">Payment</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(() => {
              const filtered = priorityFilter === "All" ? rows : rows.filter((r) => (r.priority || "Normal") === priorityFilter);
              if (filtered.length === 0) return <TableRow><TableCell colSpan={9} className="text-center text-slate-500 py-16">No applications match this filter.</TableCell></TableRow>;
              return filtered.map((r) => (
              <TableRow key={r.id} className="hover:bg-slate-50 transition-colors">
                <TableCell>
                  <Badge variant="outline" className={`${PRIORITY_TONE[r.priority || "Normal"]} font-medium text-[10px] uppercase tracking-wider gap-1.5`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${PRIORITY_DOT[r.priority || "Normal"]}`}></span>
                    {r.priority || "Normal"}
                  </Badge>
                </TableCell>
                <TableCell className="font-mono text-xs">{r.app_no}</TableCell>
                <TableCell><CustomerLink id={r.customer_id} name={r.customer_name} /></TableCell>
                <TableCell>
                  <div className="text-sm flex items-center gap-1.5">
                    {r.service_name}
                    {r.manual_service && <Badge variant="outline" className="border-amber-300 text-amber-700 bg-amber-50 text-[9px] uppercase tracking-wider">Manual</Badge>}
                  </div>
                  <div className="text-[10px] text-slate-500">{r.department}</div>
                </TableCell>
                <TableCell className="font-mono text-xs">{r.portal_app_no || <span className="text-slate-300">—</span>}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={`${STATUS_TONE[r.status]} font-medium text-[10px] uppercase tracking-wider`}>{r.status}</Badge>
                </TableCell>
                <TableCell className="text-right font-semibold">{inr(r.total)}</TableCell>
                <TableCell>
                  {r.payment_status === "Paid" ? (
                    <Badge className="bg-emerald-600 hover:bg-emerald-600 text-white">Paid · {r.payment_method}</Badge>
                  ) : r.payment_status === "Partial" ? (
                    <div className="flex flex-col gap-1">
                      <Badge className="bg-amber-500 hover:bg-amber-500 text-white text-[10px] uppercase tracking-wider w-fit">Partial · {inr(r.paid_amount || 0)} paid</Badge>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); quickCashPay(r); }}
                          data-testid={`app-quickpay-cash-${r.id}`}
                          title={`Take remaining ₹${(r.balance_due || 0).toFixed(2)} in Cash`}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-semibold uppercase tracking-wider transition-colors"
                        >
                          <CurrencyInr size={10} weight="bold" /> Balance · {inr(r.balance_due || 0)}
                        </button>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setPay({ id: r.id, method: "UPI", txn_ref: "", app: r }); }}
                          data-testid={`app-quickpay-other-${r.id}`}
                          className="inline-flex items-center px-1.5 py-0.5 rounded-full border border-amber-300 text-amber-700 bg-amber-50 hover:bg-amber-100 text-[10px] font-semibold uppercase tracking-wider transition-colors"
                        >
                          Other…
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); quickCashPay(r); }}
                        data-testid={`app-quickpay-cash-${r.id}`}
                        title="One-click cash payment"
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-semibold uppercase tracking-wider transition-colors"
                      >
                        <CurrencyInr size={12} weight="bold" /> Cash
                      </button>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setPay({ id: r.id, method: "UPI", txn_ref: "", app: r }); }}
                        data-testid={`app-quickpay-other-${r.id}`}
                        title="Pay via UPI / Card / Net Banking / Wallet"
                        className="inline-flex items-center px-2 py-1 rounded-full border border-amber-300 text-amber-700 bg-amber-50 hover:bg-amber-100 text-[11px] font-semibold uppercase tracking-wider transition-colors"
                      >
                        Other…
                      </button>
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" data-testid={`app-menu-${r.id}`}><DotsThreeVertical size={18} /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="bg-white" align="end">
                      <DropdownMenuItem onClick={() => setReceipt(r)}><Receipt size={16} className="mr-2" /> View Receipt</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setAdvanceStage({ app: r, stage: r.stage || "New Application", note: "" })} data-testid={`app-advance-${r.id}`}>
                        <Rocket size={16} className="mr-2" /> Advance Stage
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openTrack(r)} data-testid={`app-track-${r.id}`}><ClockCounterClockwise size={16} className="mr-2" /> Track &amp; AI Explain</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setEdit({ ...r })}>Edit Tracking / Priority</DropdownMenuItem>
                      {r.manual_service && isAdmin && (
                        <DropdownMenuItem onClick={() => promoteService(r)} data-testid={`app-promote-${r.id}`} className="text-emerald-700">
                          Save Service to Master
                        </DropdownMenuItem>
                      )}
                      {r.payment_status !== "Paid" && (
                        <DropdownMenuItem onClick={() => setPay({ id: r.id, method: "Cash", txn_ref: "", app: r })} data-testid={`app-pay-${r.id}`}>
                          Record Payment
                        </DropdownMenuItem>
                      )}
                      {STATUS_STEPS.map((s) => (
                        <DropdownMenuItem key={s} onClick={() => changeStatus(r.id, s)} disabled={r.status === s}>
                          Mark {s}
                        </DropdownMenuItem>
                      ))}
                      {isSuper && (
                        <DropdownMenuItem
                          onClick={() => setTransfer({ id: r.id, label: `${r.app_no} · ${r.service_name} · ${r.customer_name}`, branch_id: r.branch_id })}
                          data-testid={`app-transfer-${r.id}`}
                          className="text-orange-700"
                        >
                          Transfer to another branch
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
              ));
            })()}
          </TableBody>
        </Table>
      </Card>

      {/* Payment dialog */}
      <Dialog open={!!pay} onOpenChange={(v) => !v && setPay(null)}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader><DialogTitle style={{ fontFamily: "var(--font-heading)" }}>Record Payment</DialogTitle></DialogHeader>
          {pay && (() => {
            const total = Number(pay.app.total || 0);
            const already = Number(pay.app.paid_amount || 0);
            const balance = Math.max(0, +(total - already).toFixed(2));
            const amt = pay.amount != null ? Number(pay.amount) : balance;
            const isPartial = amt > 0 && amt < balance - 0.01;
            return (
            <div className="py-2 space-y-4">
              <div className="rounded-md bg-slate-50 border border-slate-200 p-3 space-y-0.5">
                <div className="flex justify-between text-sm"><span className="text-slate-600">Application</span><span className="font-mono">{pay.app.app_no}</span></div>
                <div className="flex justify-between text-sm"><span className="text-slate-600">Total</span><span className="font-semibold">{inr(total)}</span></div>
                {already > 0 && <div className="flex justify-between text-sm text-emerald-700"><span>Already Paid</span><span className="font-semibold">{inr(already)}</span></div>}
                <div className="flex justify-between text-sm font-semibold text-orange-700"><span>Balance Due</span><span>{inr(balance)}</span></div>
              </div>
              <F label="Method">
                <div className="grid grid-cols-5 gap-1.5">
                  {PAY_METHODS.map((m) => (
                    <button
                      key={m}
                      type="button"
                      data-testid={`pay-method-${m.replace(/\s/g, "-").toLowerCase()}`}
                      onClick={() => setPay({ ...pay, method: m })}
                      className={`px-2 py-2 rounded-md border text-xs font-medium transition-colors ${
                        pay.method === m
                          ? "bg-slate-900 text-white border-slate-900"
                          : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"
                      }`}
                    >
                      {m}
                    </button>
                  ))}
                </div>
              </F>
              <F label={`Amount (default = full balance ₹${balance})`}>
                <Input
                  data-testid="pay-amount"
                  type="number"
                  step="0.01"
                  value={pay.amount == null ? balance : pay.amount}
                  onChange={(e) => setPay({ ...pay, amount: e.target.value })}
                  placeholder={String(balance)}
                />
                {isPartial && <div className="text-[11px] text-amber-700 mt-1">Partial payment — remaining balance ₹{(balance - amt).toFixed(2)} will stay open.</div>}
              </F>
              {pay.method !== "Cash" && (
                <F label={`Txn Ref / UTR (optional)`}>
                  <Input
                    data-testid="pay-txn-ref"
                    value={pay.txn_ref || ""}
                    onChange={(e) => setPay({ ...pay, txn_ref: e.target.value })}
                    placeholder={pay.method === "UPI" ? "e.g. 4267...@ybl" : "Reference / RRN"}
                  />
                </F>
              )}
            </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPay(null)}>Cancel</Button>
            <Button data-testid="pay-confirm-btn" onClick={doPay} className="bg-orange-600 hover:bg-orange-700 text-white">Confirm Payment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit tracking dialog */}
      <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader><DialogTitle style={{ fontFamily: "var(--font-heading)" }}>Edit Application Tracking</DialogTitle></DialogHeader>
          {edit && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
              <div className="md:col-span-2 space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-600">Priority</Label>
                <div className="flex gap-2 flex-wrap">
                  {["Normal", "Urgent", "Emergency"].map((p) => (
                    <button key={p} type="button" onClick={() => setEdit({ ...edit, priority: p })}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm transition-all ${
                        (edit.priority || "Normal") === p
                          ? (p === "Emergency" ? "bg-red-600 text-white border-red-600" : p === "Urgent" ? "bg-amber-500 text-white border-amber-500" : "bg-emerald-600 text-white border-emerald-600")
                          : "bg-white text-slate-600 border-slate-300 hover:border-slate-500"
                      }`}
                    >
                      <span className={`w-2 h-2 rounded-full ${PRIORITY_DOT[p]}`}></span>{p}
                    </button>
                  ))}
                </div>
              </div>
              <F label="Portal Application No."><Input value={edit.portal_app_no || ""} onChange={(e) => setEdit({ ...edit, portal_app_no: e.target.value })} /></F>
              <F label="Token No."><Input value={edit.token_no || ""} onChange={(e) => setEdit({ ...edit, token_no: e.target.value })} /></F>
              <F label="Assigned Operator"><Input value={edit.assigned_operator || ""} onChange={(e) => setEdit({ ...edit, assigned_operator: e.target.value })} /></F>
              <F label="Expected Completion"><Input type="date" value={edit.expected_completion || ""} onChange={(e) => setEdit({ ...edit, expected_completion: e.target.value })} /></F>
              <div className="md:col-span-2"><F label="Remarks"><Textarea rows={2} value={edit.remarks || ""} onChange={(e) => setEdit({ ...edit, remarks: e.target.value })} /></F></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEdit(null)}>Cancel</Button>
            <Button onClick={saveEdit} className="bg-orange-600 hover:bg-orange-700 text-white">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Track & AI Explain dialog */}
      <Dialog open={!!track} onOpenChange={(v) => !v && (setTrack(null), setAiExplain(""), setHistory([]))}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader><DialogTitle style={{ fontFamily: "var(--font-heading)" }}>Application Tracking</DialogTitle></DialogHeader>
          {track && (
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-3 gap-3">
                <MiniInfo label="App #" value={track.app_no} mono />
                <MiniInfo label="Portal #" value={track.portal_app_no || "—"} mono />
                <MiniInfo label="Token" value={track.token_no || "—"} mono />
                <MiniInfo label="Service" value={track.service_name} />
                <MiniInfo label="Department" value={track.department} />
                <MiniInfo label="Current Status" value={track.status} />
              </div>

              <div className="pt-2 border-t border-slate-200">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Status Timeline</div>
                  <Button size="sm" onClick={askAi} disabled={aiLoading} data-testid="ai-explain-btn" className="bg-purple-600 hover:bg-purple-700 text-white gap-2 h-8 text-xs">
                    <Sparkle size={14} weight="fill" />{aiLoading ? "Asking AI…" : "Ask AI to Explain"}
                  </Button>
                </div>
                <div className="space-y-2">
                  {history.length === 0 && <div className="text-sm text-slate-500 py-6 text-center">No activity yet.</div>}
                  {history.map((e) => (
                    <div key={`${e.status}-${e.at}`} className="flex gap-3 pb-2 border-b border-slate-100 last:border-0">
                      <div className="w-2 h-2 mt-1.5 rounded-full bg-orange-500 shrink-0" />
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <Badge variant="outline" className={`${STATUS_TONE[e.status] || "border-slate-200"} font-medium text-[10px] uppercase tracking-wider`}>{e.status}</Badge>
                          <span className="text-[10px] text-slate-500 font-mono">{e.at?.slice(0, 16).replace("T", " ")}</span>
                        </div>
                        {e.note && <div className="text-xs text-slate-700 mt-1">{e.note}</div>}
                        {e.by && <div className="text-[10px] text-slate-400 mt-0.5">by {e.by}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {aiExplain && (
                <div className="rounded-md border border-purple-200 bg-purple-50/60 p-4" data-testid="ai-explanation">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 rounded-full bg-purple-600 flex items-center justify-center"><Sparkle size={12} weight="fill" color="#fff" /></div>
                    <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-purple-700">AI Explanation</span>
                  </div>
                  <div className="text-sm text-slate-800 leading-relaxed whitespace-pre-wrap">{aiExplain}</div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Receipt dialog */}
      <Dialog open={!!receipt} onOpenChange={(v) => !v && setReceipt(null)}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader><DialogTitle style={{ fontFamily: "var(--font-heading)" }}>Receipt</DialogTitle></DialogHeader>
          {receipt && <Invoice app={receipt} />}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReceipt(null)}>Close</Button>
            <Button className="bg-slate-900 hover:bg-slate-800 gap-2" onClick={() => window.print()} data-testid="receipt-print-btn"><Printer size={16} /> Print</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Advance Stage dialog */}
      <Dialog open={!!advanceStage} onOpenChange={(v) => !v && setAdvanceStage(null)}>
        <DialogContent className="max-w-lg bg-white" data-testid="advance-stage-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "var(--font-heading)" }} className="flex items-center gap-2">
              <Rocket size={20} weight="duotone" className="text-orange-500" /> Advance Application Stage
            </DialogTitle>
          </DialogHeader>
          {advanceStage && (
            <div className="space-y-4">
              <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 text-sm">
                <div className="font-mono text-[11px] text-slate-500">{advanceStage.app.app_number || advanceStage.app.app_no}</div>
                <div className="font-semibold text-slate-900">{advanceStage.app.service_name}</div>
                <div className="text-xs text-slate-600">Customer: {advanceStage.app.customer_name}</div>
                <div className="mt-1.5 flex items-center gap-2 text-xs">
                  <span className="text-slate-500">Current stage:</span>
                  <span className="font-semibold px-2 py-0.5 rounded bg-white border border-slate-200">{advanceStage.app.stage || "New Application"}</span>
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600 mb-1.5 block">Move to stage *</Label>
                <Select value={advanceStage.stage} onValueChange={(v) => setAdvanceStage((s) => ({ ...s, stage: v }))}>
                  <SelectTrigger className="h-10" data-testid="advance-stage-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-80 bg-white">
                    {STAGE_WORKFLOW.map((s, i) => (
                      <SelectItem key={s} value={s}>
                        <span className="inline-flex items-center gap-2">
                          <span className="w-5 h-5 rounded-full bg-slate-100 text-slate-600 text-[10px] font-bold flex items-center justify-center">{i + 1}</span>
                          {s}
                        </span>
                      </SelectItem>
                    ))}
                    <SelectItem value="Rejected">
                      <span className="inline-flex items-center gap-2 text-red-600">
                        <span className="w-5 h-5 rounded-full bg-red-100 text-red-600 text-[10px] font-bold flex items-center justify-center">X</span>
                        Rejected
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold text-slate-600 mb-1.5 block">Note (visible to customer on /track)</Label>
                <Textarea
                  value={advanceStage.note}
                  onChange={(e) => setAdvanceStage((s) => ({ ...s, note: e.target.value }))}
                  placeholder="e.g. Documents verified · sent to portal for submission"
                  rows={3}
                  data-testid="advance-stage-note"
                  className="resize-none"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdvanceStage(null)}>Cancel</Button>
            <Button
              className="bg-orange-600 hover:bg-orange-700 text-white gap-2"
              disabled={!advanceStage?.stage}
              data-testid="advance-stage-submit"
              onClick={async () => {
                try {
                  await api.post(`/applications/${advanceStage.app.id}/stage`, {
                    stage: advanceStage.stage,
                    note: advanceStage.note,
                  });
                  toast.success(`Moved to "${advanceStage.stage}"`);
                  setAdvanceStage(null);
                  load();
                } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
              }}
            >
              <Rocket size={14} weight="fill" /> Advance
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <TransferDialog
        open={!!transfer}
        onOpenChange={(v) => !v && setTransfer(null)}
        entity="application"
        entityId={transfer?.id}
        entityLabel={transfer?.label}
        fromBranchId={transfer?.branch_id}
        onTransferred={load}
      />
    </div>
  );
}

function F({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-600">{label}</Label>
      {children}
    </div>
  );
}
function NumField({ label, value, onChange, testid }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[11px] uppercase tracking-[0.15em] font-semibold text-slate-600">{label}</Label>
      <Input data-testid={testid} type="number" value={value} onChange={(e) => onChange(Number(e.target.value))} />
    </div>
  );
}
function MiniInfo({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold">{label}</div>
      <div className={`text-sm text-slate-900 ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
    </div>
  );
}
function SectionHeading({ children }) {
  return <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-orange-600 mt-5 mb-3 pb-1 border-b border-slate-200">{children}</div>;
}
function Row({ label, value }) {
  return <div className="flex justify-between text-sm py-1"><span className="text-slate-600">{label}</span><span className="font-medium text-slate-900">{value}</span></div>;
}
function Invoice({ app }) {
  const qrData = `${app.app_no}|${app.portal_app_no || ""}|${app.total}`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=110x110&data=${encodeURIComponent(qrData)}`;
  return (
    <div className="print-area py-2" data-testid="receipt-content">
      <div className="text-center border-b border-dashed border-slate-300 pb-3 mb-3">
        <div className="font-bold text-lg" style={{ fontFamily: "var(--font-heading)" }}>PRIYANKA ENTERPRISE · ERP</div>
        <div className="text-xs text-slate-500">GST Invoice · Aaple Sarkar / MahaOnline</div>
      </div>
      <div className="text-xs space-y-1">
        <Row label="Invoice #" value={app.app_no} />
        <Row label="Portal App #" value={app.portal_app_no || "—"} />
        <Row label="Token" value={app.token_no || "—"} />
        <Row label="Date" value={new Date(app.created_at).toLocaleString()} />
        <Row label="Customer" value={app.customer_name} />
        <Row label="Service" value={app.service_name} />
        {app.department && <Row label="Department" value={app.department} />}
      </div>
      <div className="border-t border-dashed border-slate-300 my-3" />
      <div className="text-xs space-y-1">
        {app.govt_fee > 0 && <Row label="Government Fee" value={inr(app.govt_fee)} />}
        {app.service_charge > 0 && <Row label="Service Charge" value={inr(app.service_charge)} />}
        {app.printing_charge > 0 && <Row label="Printing" value={inr(app.printing_charge)} />}
        {app.scanning_charge > 0 && <Row label="Scanning" value={inr(app.scanning_charge)} />}
        {app.affidavit_charge > 0 && <Row label="Affidavit" value={inr(app.affidavit_charge)} />}
        {app.lamination_charge > 0 && <Row label="Lamination" value={inr(app.lamination_charge)} />}
        {app.discount > 0 && <Row label="Discount" value={`- ${inr(app.discount)}`} />}
        <Row label={`GST (${app.gst_percent}%)`} value={inr(app.gst_amount)} />
      </div>
      <div className="border-t border-dashed border-slate-300 my-3" />
      <div className="flex justify-between items-center">
        <span className="text-sm font-semibold">TOTAL</span>
        <span className="text-2xl font-bold text-orange-600" style={{ fontFamily: "var(--font-heading)" }}>{inr(app.total)}</span>
      </div>
      <div className="flex items-center justify-between gap-4 mt-4 pt-3 border-t border-dashed border-slate-300">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 mb-0.5">Payment</div>
          <div className="text-sm">{app.payment_status}{app.payment_method ? ` · ${app.payment_method}` : ""}</div>
          {app.payment_txn_ref && <div className="text-[10px] text-slate-500 font-mono mt-0.5">Ref: {app.payment_txn_ref}</div>}
        </div>
        <img src={qrUrl} alt="QR" className="w-24 h-24 border border-slate-200 rounded" />
      </div>
      <div className="text-center text-[10px] text-slate-400 mt-3">Scan the QR to open this invoice · Thank you for your visit</div>
    </div>
  );
}
